import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { AdminAuthProvider } from "@/context/AdminAuth";
import { ResourceFilesProvider } from "@/context/ResourceFiles";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import About from "@/pages/About";
import Industries from "@/pages/Industries";
import Contact from "@/pages/Contact";
import Resources from "@/pages/Resources";
import Insights from "@/pages/Insights";
import Article from "@/pages/Article";
import CaseStudies from "@/pages/CaseStudies";
import Privacy from "@/pages/Privacy";
import NotFound from "@/pages/NotFound";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/** Legacy nested solution URLs now live one level up. */
const LegacySolution = () => {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace("/services/solutions/", "/services/")} replace />;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#DDE3F2] antialiased">
      {!isAdmin && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<ServicePage />} />
          <Route path="/services/solutions" element={<ServicePage />} />
          <Route path="/services/solutions/:slug" element={<LegacySolution />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/about" element={<About />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<Article />} />
          <Route path="/blog" element={<Navigate to="/insights" replace />} />
          <Route path="/blogs" element={<Navigate to="/insights" replace />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </div>
  );
};

export default function App() {
  React.useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <ResourceFilesProvider>
          <ScrollToTop />
          <Seo />
          <Shell />
          <Toaster theme="dark" position="bottom-right" />
        </ResourceFilesProvider>
      </AdminAuthProvider>
    </BrowserRouter>
  );
}
